import { useEffect, useRef, useState } from 'react';

import loadable from '@loadable/component';
import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ArrowIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkInfoBanner from '@skyscanner-internal/backpack-web/bpk-component-info-banner';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import { BackendContextProvider } from '../../hotels-utils/components-hotel-card-adjacent/backend-context';
import BackendGateway from '../../hotels-utils/services/backend-gateway/backend-gateway';
import EventSource from '../../hotels-utils/services/event-source';
import { I18nProvider } from '../../hotels-utils/services/i18n';
import I18nService from '../../hotels-utils/services/i18n/i18n-service';
import logger from '../../hotels-utils/services/logger';
import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import FilterLinks from '../FilterLinks';
import FilterLinksMobile from '../FilterLinks/FilterLinksMobile';
import HotelSearchResultsDisplay from '../HotelSearchResultsDisplay';
import LandingPageDataStore from '../HotelsCommon/LandingPageDataStore/LandingPageDataStore';
import { isEmptySearchResults } from '../HotelsCommon/utils';
import HotelsIndicativePricesLegalDisclaimer from '../HotelsIndicativePricesLegalDisclaimer';
import SeoBannerSearchHotels from '../SeoBannerSearchHotels';
import SortingLinks from '../SortingLinks';
import SortingLinksMobile from '../SortingLinks/SortingLinksMobile';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { Filter } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FilterLinks';
import type { HotelSearchResultsWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HotelSearchResultsWrapperProps';
import type { HotelsIndicativePricesLegalDisclaimerProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/HotelsIndicativePricesLegalDisclaimer';

import STYLES from './HotelSearchResultsWrapper.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const AlignedArrowIcon = withButtonAlignment(withRtlSupport(ArrowIcon));

type HotelSearchResults = {
  hotelCards: [];
  loading: boolean;
};

type AboutSearchResultsProps = {
  market: string;
  translations: Record<string, string>;
  indicativePricesLegalDisclaimerProps?: HotelsIndicativePricesLegalDisclaimerProps;
};

const AboutSearchResults = ({
  indicativePricesLegalDisclaimerProps = {},
  market,
  translations,
}: AboutSearchResultsProps) => {
  if (market === 'FR' && translations) {
    const detailsUrl =
      'https://www.skyscanner.fr/presse/en-savoir-plus-sur-skyscanner';
    const message = (
      <div className={STYLES.AboutSearchResults}>
        <div className={STYLES.AboutSearchResults__desktopBanner}>
          <div>
            <span className={STYLES.AboutSearchResults__title}>
              {translations.SearchResults_label_aboutBestOrderTitle}
            </span>
            &nbsp;
            {translations.AboutSearchResults_aboutBestOrder_content}
          </div>
          <div className={STYLES.AboutSearchResults__secondParagraph}>
            <span className={STYLES.AboutSearchResults__title}>
              {translations.SearchResults_label_aboutWhereTitle}
            </span>
            &nbsp;
            {translations.AboutSearchResults_searchResultsOrigin_content}
            &nbsp;
            <BpkLink href={detailsUrl} blank>
              {translations.SearchResults_link_aboutLearnMore}
            </BpkLink>
          </div>
        </div>

        <div className={STYLES.AboutSearchResults__mobileBanner}>
          <span className={STYLES.AboutSearchResults__title}>
            {translations.SearchResults_label_aboutWhereTitle}
          </span>
          &nbsp;
          {translations.AboutSearchResults_searchResultsOrigin_content}
          &nbsp;
          <BpkLink href={detailsUrl} blank>
            {translations.SearchResults_link_aboutLearnMore}
          </BpkLink>
        </div>
      </div>
    );

    return (
      <div className={STYLES.HotelSearchResultsWrapper__aboutSearchResults}>
        <BpkInfoBanner message={message} type="info" />
      </div>
    );
  }

  const {
    desktopDisclaimer,
    fullWidth = true,
    mobileDisclaimer,
    useDangerouslySetInnerHtml = false,
  } = indicativePricesLegalDisclaimerProps;

  const desktopDisclaimerValue = desktopDisclaimer ?? '';
  const mobileDisclaimerValue = mobileDisclaimer ?? '';

  if (desktopDisclaimerValue || mobileDisclaimerValue) {
    return (
      <div className={STYLES.HotelSearchResultsWrapper__aboutSearchResults}>
        <HotelsIndicativePricesLegalDisclaimer
          desktopDisclaimer={desktopDisclaimerValue}
          mobileDisclaimer={mobileDisclaimerValue}
          useDangerouslySetInnerHtml={useDangerouslySetInnerHtml}
          fullWidth={fullWidth}
          isChild
        />
      </div>
    );
  }

  return null;
};

const HotelSearchResultsWrapper = (props: HotelSearchResultsWrapperProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const ctaComponentName = `${rawComponentName}_cta`;

  const {
    backendUrl,
    cachedSearchResults,
    cldr,
    clientStrings,
    culture,
    dayViewUrl,
    destination,
    filterLinksProps,
    hotelCardsLimit,
    indicativePricesLegalDisclaimerProps,
    isMobile: ssrIsMobile,
    removePartnerRedirectLinks,
    showReviewImage,
    sortingLinksProps,
    stay,
    translations,
    userContext,
  } = props;

  const [hotelSearchResults, setHotelSearchResults] = useState<
    HotelSearchResults | undefined
  >(undefined);
  const [isMobile, setIsMobile] = useState(ssrIsMobile);

  const i18n = useRef(
    I18nService({
      translations,
      cldr,
      culture,
    }),
  ).current;

  const backendGateway = useRef(
    BackendGateway({
      backendUrl,
      userContext,
      eventSource: EventSource(logger, false),
      i18n,
    }),
  ).current;

  const store = useRef(LandingPageDataStore({ backendGateway })).current;

  const media = useRef<MediaQueryList | null>(null);

  // Set initial hotel search results if cached
  useEffect(() => {
    if (cachedSearchResults && cachedSearchResults.hotelCards) {
      store.setHotelSearchResults(cachedSearchResults);
      setHotelSearchResults(
        store.getHotelSearchResults() as HotelSearchResults,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to store updates
  useEffect(() => {
    const getDataFromStoreCallback = () => {
      setHotelSearchResults(
        store.getHotelSearchResults() as HotelSearchResults,
      );
    };
    store.addListener(getDataFromStoreCallback);
    return () => {
      store.removeListener(getDataFromStoreCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle mobile detection
  useEffect(() => {
    media.current = window.matchMedia(BREAKPOINTS.MOBILE);
    const updateIsMobile = () => setIsMobile(media.current?.matches ?? false);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => {
      window.removeEventListener('resize', updateIsMobile);
    };
  }, []);

  const hasFilters = filterLinksProps && filterLinksProps?.filters.length > 0;

  if (isEmptySearchResults(hotelSearchResults)) {
    return (
      <div
        data-tracking-element-id={rawComponentName}
        data-tracking-common-params={buildMBDParam(rawComponentName)}
      >
        <I18nProvider value={i18n}>
          <AboutSearchResults
            market={culture.market}
            translations={translations}
            indicativePricesLegalDisclaimerProps={
              indicativePricesLegalDisclaimerProps
            }
          />

          {destination && dayViewUrl && (
            <SeoBannerSearchHotels
              dayViewUrl={dayViewUrl}
              clientStrings={clientStrings}
            />
          )}
        </I18nProvider>
      </div>
    );
  }

  return (
    <BackendContextProvider value={backendGateway}>
      <I18nProvider value={i18n}>
        <div
          className={STYLES.HotelSearchResultsWrapper}
          data-tracking-element-id={rawComponentName}
          data-tracking-common-params={buildMBDParam(rawComponentName)}
        >
          {hasFilters && (
            <div className={STYLES.HotelSearchResultsWrapper__sidebar}>
              <div className={STYLES.HotelSearchResultsWrapper__sidebarContent}>
                <FilterLinks {...filterLinksProps} />
              </div>
            </div>
          )}

          <div className={STYLES.HotelSearchResultsWrapper__header}>
            {sortingLinksProps?.h2Content && (
              <div className={STYLES.HotelSearchResultsWrapper__title}>
                <BpkSectionHeader title={sortingLinksProps.h2Content} />
              </div>
            )}

            <div className={STYLES.HotelSearchResultsWrapper__headerDesktop}>
              {sortingLinksProps && (
                <SortingLinks {...sortingLinksProps} h2Content={undefined} />
              )}
            </div>

            <div className={STYLES.HotelSearchResultsWrapper__headerMobile}>
              <div
                className={STYLES.HotelSearchResultsWrapper__headerChipGroup}
              >
                {hasFilters && (
                  <FilterLinksMobile
                    label={translations.FilteringLinks_filters}
                    title={translations.FilteringLinks_filter_hotels_by}
                    filters={filterLinksProps?.filters as Filter[]}
                  />
                )}
                {sortingLinksProps && (
                  <SortingLinksMobile
                    label={translations.SortingLinks_sortBy}
                    {...sortingLinksProps}
                  />
                )}
              </div>
            </div>
          </div>

          <div className={STYLES.HotelSearchResultsWrapper__content}>
            <AboutSearchResults
              market={culture.market}
              translations={translations}
              indicativePricesLegalDisclaimerProps={
                indicativePricesLegalDisclaimerProps
              }
            />

            <HotelSearchResultsDisplay
              destination={destination}
              stay={stay}
              fromSeoPage
              hotelSearchResults={hotelSearchResults}
              hotelCardsLimit={hotelCardsLimit}
              isMobile={isMobile}
              clientStrings={clientStrings}
              removePartnerRedirectLinks={removePartnerRedirectLinks}
              dayViewUrl={dayViewUrl}
              showReviewImage={showReviewImage}
            />

            {dayViewUrl && (
              <BpkButton
                className={STYLES.HotelSearchResultsWrapper__callToActionButton}
                href={dayViewUrl}
                rel="nofollow"
                data-tracking-element-id={`${ctaComponentName}_view_more`}
                data-tracking-common-params={buildMBDParam(ctaComponentName, {
                  cta_type: 'view_more',
                })}
              >
                {clientStrings.searchResultViewMoreLabel}
                <AlignedArrowIcon />
              </BpkButton>
            )}
          </div>
        </div>
      </I18nProvider>
    </BackendContextProvider>
  );
};

export default (props: HotelSearchResultsWrapperProps) => (
  <IntersectionObserverWrapper
    threshold={0.2}
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.HOTEL_SEARCH_RESULTS,
      })
    }
  >
    <HotelSearchResultsWrapper {...props} />
  </IntersectionObserverWrapper>
);
